import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import type { BillRunCoverage } from '../../api/types';

// ---------------------------------------------------------------------------
// TV-3 — the Create Bill Run pre-flight coverage check.
//
// `bill-calculator` already refuses to price a meter with no tariff version for
// the period, and raises a run-level flag that blocks approval. That is the
// authoritative backstop. This hook asks the SAME question before the run is
// queued, so the operator fixes it while it is cheap.
//
// THREE DECISIONS, all about what to do when the answer is not yet known.
//
// 1. THE GUARD BLOCKS ON A KNOWN-BAD VERDICT, NEVER ON AN UNKNOWN.
//    A pre-flight check that blocked on "I have not answered yet" would make a
//    slow or failing endpoint indistinguishable from a broken tariff, and could
//    stop billing entirely. The calculator is the backstop, so permitting an
//    unknown is not silent damage — it degrades to the behaviour that shipped
//    before this guard existed, which is a refusal plus a flag.
//
// 2. AN ENDPOINT FAILURE IS PERMISSIVE AND VISIBLE.
//    Create stays enabled; `unavailable` is set and the dialog renders a
//    warning with a Retry. Neither of the two failure modes the brief rules
//    out happens: it does not silently permit (the warning names the state and
//    says the calculator will still refuse) and it cannot permanently block (a
//    failing check never disables Create).
//
// 3. AN EXISTING BLOCK SURVIVES A RE-CHECK; A CLEAN VERDICT DOES NOT.
//    When the selection or period changes while a block is showing, the block
//    stays up — marked as re-checking — until the new answer arrives. That is
//    asymmetric on purpose: it means the transition that can create damage
//    (blocked → briefly enabled → clicked) cannot happen, while the common case
//    of an already-clean selection never shows a spinner or a disabled button.
//    Deselecting the offending pocket therefore clears the block when the new
//    verdict returns, roughly half a second later, rather than instantly.
//
//    The residual gap is a clean selection edited into a bad one and submitted
//    within the debounce window (~350ms) plus one round trip. The calculator
//    catches that case, which is what it is for.
// ---------------------------------------------------------------------------

/**
 * How long to wait after the last edit before asking. Long enough that dragging
 * through a pocket list or typing a date does not fire a request per keystroke;
 * short enough that the answer is there before an operator reaches Create.
 */
const DEBOUNCE_MS = 350;

export interface CoverageGuard {
  /**
   * The verdict worth rendering, or null when there is nothing to say. Null for
   * a clean selection — a clean selection shows NOTHING, no summary and no
   * spinner.
   */
  coverage: BillRunCoverage | null;
  /** True when Create must be disabled. Only ever set by a real verdict. */
  blocked: boolean;
  /** Set when the check could not be answered. Does NOT block Create. */
  unavailable: string | null;
  /** A check is in flight over an already-showing block. */
  rechecking: boolean;
  /** Re-ask now, ignoring the debounce. Wired to the failure state's Retry. */
  retry: () => void;
}

export interface CoverageGuardInput {
  /** False while the dialog is closed — nothing is asked then. */
  enabled: boolean;
  selectedPocketIds: ReadonlySet<string>;
  periodStart: string;
  periodEnd: string;
}

export function useCoverageGuard({
  enabled,
  selectedPocketIds,
  periodStart,
  periodEnd,
}: CoverageGuardInput): CoverageGuard {
  const [coverage, setCoverage] = useState<BillRunCoverage | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [rechecking, setRechecking] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // A Set identity changes every render in the parent, so the effect keys off a
  // canonical string instead. Sorted, so re-selecting the same pockets in a
  // different order is not a new question.
  const pocketKey = [...selectedPocketIds].sort().join(',');

  // Only the newest request may write state — an earlier, slower answer for a
  // superseded selection must never overwrite a newer one.
  const latestRequest = useRef(0);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    const pocketIds = pocketKey.length > 0 ? pocketKey.split(',') : [];
    // `periodEnd <= periodStart` is rejected by the endpoint, and the operator
    // is mid-edit — asking would only produce a 400 and a scary warning.
    const askable =
      enabled && pocketIds.length > 0 && periodStart !== '' && periodEnd > periodStart;

    if (!askable) {
      latestRequest.current += 1;
      setCoverage(null);
      setUnavailable(null);
      setRechecking(false);
      return;
    }

    const requestId = ++latestRequest.current;
    // Decision 3: hold an existing block while re-checking; drop a clean or
    // failed verdict immediately so neither goes stale on screen.
    setUnavailable(null);
    setCoverage((prev) => (prev && prev.meters_blocked > 0 ? prev : null));
    setRechecking(true);

    const timer = setTimeout(() => {
      api
        .getBillRunCoverage({ pocketIds, periodStart, periodEnd })
        .then((res) => {
          if (latestRequest.current !== requestId) return;
          setCoverage(res.data.meters_blocked > 0 ? res.data : null);
          setUnavailable(null);
        })
        .catch((err: unknown) => {
          if (latestRequest.current !== requestId) return;
          // Drop any held block: it was an answer to a different question, and
          // keeping it alongside "could not check" would be two claims at once.
          setCoverage(null);
          setUnavailable(err instanceof Error ? err.message : 'Coverage check failed');
        })
        .finally(() => {
          if (latestRequest.current !== requestId) return;
          setRechecking(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [enabled, pocketKey, periodStart, periodEnd, attempt]);

  return {
    coverage,
    blocked: coverage !== null && coverage.meters_blocked > 0,
    unavailable,
    rechecking: rechecking && coverage !== null,
    retry,
  };
}
